import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createStyles,
  makeStyles,
  Menu,
  MenuItem,
  useMediaQuery,
  useScrollTrigger,
} from '@material-ui/core';
import clsx from 'clsx';

import { sessionQuery } from '@lib/state';
import { useObservable, EnvironmentBanner } from '@lib/common';
import { SiteFooter } from './site-footer';
import './guest-site.css';
import LogoMark from 'app/assets/landing/armadillo-logo-mark.svg';
import LogoWordmark from 'app/assets/landing/armadillo-logo.svg';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
    },
    content: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      marginTop: 72,
      '@media (min-width:961px)': {
        marginTop: 76,
      },
    },
  })
);

const sections = [
  ['services', 'Services'],
  ['locations', 'Locations'],
  ['about', 'About'],
  ['outro', 'Contact Us'],
];

const guestLinks = [
  {
    to: '/profile/stays',
    label: 'My Stays',
    testid: 'navMyStaysButton',
    icon: (
      <svg
        className="guest-menu-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="5" width="18" height="16" rx="2"></rect>
        <path d="M16 3v4M8 3v4M3 11h18m-14 4h2m4 0h2"></path>
      </svg>
    ),
  },
  {
    to: '/profile',
    label: 'My Account',
    testid: 'navMyAccountButton',
    icon: (
      <svg
        className="guest-menu-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="8" r="4"></circle>
        <path d="M4 21v-2a8 8 0 0 1 16 0v2"></path>
      </svg>
    ),
  },
  {
    to: '/profile/key',
    label: 'My Key',
    testid: 'navMyKeyButton',
    icon: (
      <svg
        className="guest-menu-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="7" cy="8" r="5"></circle>
        <path d="M12 8h10M17 8v4M21 8v4"></path>
      </svg>
    ),
  },
];

const authIcon = (
  <svg
    className="guest-menu-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5M3 12h12m-5-5 5 5-5 5"></path>
  </svg>
);

const authLink = (isLoggedIn?: boolean) =>
  isLoggedIn
    ? { to: '/account/signout', label: 'Log Out', icon: authIcon, testid: 'menuLogoutButton' }
    : { to: '/account/signin', label: 'Log In', icon: authIcon, testid: 'menuLoginButton' };

export const NavLayout: React.FC = ({ children }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const isLoggedIn = useObservable(sessionQuery.isLoggedIn, 'async');
  const isMobile = useMediaQuery('(max-width:960px)', { noSsr: true });
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 60 });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMenu = () => setMenuAnchor(null);
  // panel never survives a resize to desktop
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  // Book Now: on mobile opens the sheet via ?booking (keeps promo etc.), on desktop scrolls to the card
  const bookingSearch = new URLSearchParams(location.search);
  bookingSearch.set('booking', '');
  const bookNow = isMobile
    ? { pathname: '/', search: `?${bookingSearch}` }
    : { pathname: '/', hash: '#booking' };
  const links = [...guestLinks, authLink(isLoggedIn)];

  return (
    <div className={classes.root}>
      <div className="guest-site">
        <header className={clsx('site-header', scrolled && 'scrolled')}>
          <div className="no-reset">
            <EnvironmentBanner />
          </div>
          <div className="header-inner container">
            <Link
              to="/"
              className="logo"
              aria-label="Armadillo Hotel home"
              data-testid="navHomeButton"
            >
              <img src={LogoMark} alt="" className="logo-mark" />
              <img src={LogoWordmark} alt={t('title')} className="logo-wordmark" />
            </Link>

            <nav className="header-nav primary-site-nav" aria-label="Primary">
              {sections.map(([id, label]) => (
                <Link key={id} className="header-nav-link" to={{ pathname: '/', hash: `#${id}` }}>
                  {label}
                </Link>
              ))}
            </nav>

            <div className="header-actions">
              <Link to={bookNow} className="btn btn-primary btn-sm" data-testid="navBookNowButton">
                Book Now
              </Link>

              <button
                type="button"
                className={clsx('account-btn guest-menu', menuAnchor && 'open')}
                onClick={e => setMenuAnchor(e.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={!!menuAnchor}
                data-testid="navGuestMenuButton"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                  <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6"></circle>
                  <path
                    d="M5 21c0-3.5 3-6 7-6s7 2.5 7 6"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  ></path>
                </svg>
                <span>Guest Menu</span>
                <span className="guest-chevron" aria-hidden="true">
                  ⌄
                </span>
              </button>
              <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={closeMenu}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                classes={{ paper: 'guest-site guest-dropdown' }}
                MenuListProps={{ disablePadding: true, 'aria-label': 'Guest services' }}
              >
                {links.map(l => (
                  <MenuItem
                    key={l.to}
                    component={Link}
                    to={l.to}
                    onClick={closeMenu}
                    disableRipple
                    data-testid={l.testid}
                  >
                    {l.icon}
                    <span>{l.label}</span>
                  </MenuItem>
                ))}
              </Menu>

              <button
                type="button"
                className={clsx('mobile-menu-btn', mobileOpen && 'open')}
                onClick={() => setMobileOpen(o => !o)}
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-controls="mobile-nav"
                aria-expanded={mobileOpen}
                data-testid="navHamburgerButton"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>

          {mobileOpen && (
            <nav
              className="mobile-nav open"
              id="mobile-nav"
              aria-label="Mobile navigation"
              onClick={() => setMobileOpen(false)}
            >
              {sections.map(([id, label]) => (
                <Link key={id} to={{ pathname: '/', hash: `#${id}` }}>
                  {label}
                </Link>
              ))}
              <p className="mobile-section-label">Guest Menu</p>
              {links.map(l => (
                <Link key={l.to} to={l.to} data-testid={l.testid}>
                  {l.icon}
                  <span>{l.label}</span>
                </Link>
              ))}
            </nav>
          )}
        </header>
      </div>
      <div className={classes.content}>{children}</div>
      <SiteFooter />
    </div>
  );
};
